// vim: set ts=2 sts=2 sw=2 et:

import React, { useState } from "react";
import { connect } from "react-redux";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import type { Dispatch } from "redux";

import type { ApplyWebPatchAction } from "../../types/actionTypes";
import type { GlobalState } from "../../types/stateTypes";
import { fetchWrpfBundle } from "./fetchWrpfBundle";
import type { WebPatch } from "./pullFromWeb";
import { applyOpToEntry, buildWebPatch, mergedMeet, selectedOps } from "./pullFromWeb";
import WrpfPullFromWebModal from "./WrpfPullFromWebModal";

type OwnProps = {
  /** Botón a lo ancho, como los de Inicio. */
  block?: boolean;
};

type StateProps = {
  global: GlobalState;
};

type DispatchProps = {
  dispatch: Dispatch;
};

type Props = OwnProps & StateProps & DispatchProps;

/** URL del bundle: la del handoff si la hay; si no, la misma API que /results. */
export function bundleUrlFrom(session: GlobalState["wrpfSession"]): string | null {
  if (session.handoffBundleUrl) return session.handoffBundleUrl;
  if (!session.callbackUrl) return null;
  try {
    return new URL("bundle", session.callbackUrl).toString();
  } catch {
    return session.callbackUrl.replace(/\/results\/?$/, "/bundle");
  }
}

/**
 * "Traer cambios de la web": baja el bundle fresco del torneo (tandas aplicadas en el wizard y
 * pesajes cargados en la página de Pesaje) y muestra qué cambiaría antes de escribir nada.
 */
const WrpfPullFromWebButtonInner = ({ global, dispatch, block }: Props) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pending, setPending] = useState<{ patch: WebPatch; fetchedAt: Date } | null>(null);

  const session = global.wrpfSession;
  const bundleUrl = bundleUrlFrom(session);
  if (!session.tournamentId || !session.callbackToken || !bundleUrl) return null;

  const handleFetch = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const incoming = await fetchWrpfBundle(bundleUrl, session.callbackToken!);
      if (incoming._wrpf?.tournamentId !== session.tournamentId) {
        throw new Error("La web devolvió otro torneo: no se trae nada.");
      }
      const patch = buildWebPatch({
        localEntries: global.registration.entries,
        localAnchors: session.entryAnchors,
        localMeet: global.meet,
        incoming,
        baseline: session.webBaseline,
      });
      if (patch.rows.length === 0 && patch.meet === null && patch.newInWeb.length === 0) {
        setNotice("No hay cambios: esta PC ya tiene lo mismo que la web.");
        return;
      }
      setPending({ patch, fetchedAt: new Date() });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleApply = (selectedKeys: ReadonlySet<string>, applyMeet: boolean) => {
    if (!pending) return;
    const { patch } = pending;
    const ops = selectedOps(patch, selectedKeys);
    const entriesAfter = global.registration.entries.map((entry) =>
      ops.filter((op) => op.entryId === entry.id).reduce(applyOpToEntry, entry),
    );
    const action: ApplyWebPatchAction = {
      type: "APPLY_WEB_PATCH",
      ops,
      meet: applyMeet && patch.meet ? mergedMeet(patch.meet, entriesAfter) : null,
      baseline: patch.processedBaseline,
    };
    dispatch(action);
    setPending(null);
    setNotice(`Aplicado: ${ops.length} cambio(s)${action.meet ? " y días/tarimas del torneo" : ""}.`);
  };

  return (
    <div style={{ marginTop: "8px", marginBottom: "8px" }}>
      <Button variant="outline-primary" block={block} onClick={handleFetch} disabled={busy}>
        {busy ? "Consultando la web…" : "Traer cambios de la web (tandas y pesaje)"}
      </Button>
      {error && (
        <Alert variant="danger" className="mt-2 mb-0">
          {error}
        </Alert>
      )}
      {notice && (
        <Alert variant="success" className="mt-2 mb-0" onClose={() => setNotice(null)} dismissible>
          {notice}
        </Alert>
      )}
      {pending && (
        <WrpfPullFromWebModal
          show
          patch={pending.patch}
          fetchedAt={pending.fetchedAt}
          entries={global.registration.entries}
          meet={global.meet}
          language={global.language}
          onCancel={() => setPending(null)}
          onApply={handleApply}
        />
      )}
    </div>
  );
};

const mapState = (state: GlobalState): StateProps => ({ global: state });

export const WrpfPullFromWebButton = connect(mapState, (dispatch: Dispatch): DispatchProps => ({ dispatch }))(
  WrpfPullFromWebButtonInner,
);

export default WrpfPullFromWebButton;
